import React, { useState, useEffect } from 'react';

const StepThinking = ({ brandSignup, onComplete, onSkipToDashboard, loading, error }) => {
  const [dots, setDots] = useState('');
  const [thinkingText, setThinkingText] = useState('Analyzing your product details');
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);
  const [aiStatus, setAiStatus] = useState(brandSignup.ai_enrichment_status || 'processing');
  const [pollCount, setPollCount] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const thinkingMessages = [
    'Analyzing your product details',
    'Suggesting the best category',
    'Identifying materials needed',
    'Finding manufacturing capabilities',
    'Determining helpful services',
    'Almost ready'
  ];

  useEffect(() => {
    // Start minimum timer (4 seconds)
    const minimumTimer = setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, 4000);

    // Animate dots
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    // Cycle through messages
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setThinkingText(thinkingMessages[messageIndex]);
    }, 3000);

    return () => {
      clearTimeout(minimumTimer);
      clearInterval(dotsInterval);
      clearInterval(messageInterval);
    };
  }, []);

  // Poll for AI enrichment status (only if not already completed)
  useEffect(() => {
    // If AI is already completed, don't poll
    if (aiStatus === 'completed' || aiStatus === 'failed') {
      return;
    }

    let pollInterval;
    const maxPolls = 15;

    const pollAiStatus = async () => {
      // Stop polling after 15 attempts
      if (pollCount >= maxPolls) {
        console.log('AI polling timeout - moving on after 15 attempts');
        setAiStatus('failed'); // Treat as failed to allow completion
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        return;
      }

      setPollCount(prev => prev + 1);

      try {
        const response = await fetch(`/brand_signup/${brandSignup.id}/enrichment_status`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          }
        });

        if (response.ok) {
          const data = await response.json();

          // Only update if status changed
          if (data.status !== aiStatus) {
            setAiStatus(data.status);

            // Stop polling if completed or failed
            if (data.status === 'completed' || data.status === 'failed') {
              if (pollInterval) {
                clearInterval(pollInterval);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error polling AI status:', error);
      }
    };

    // Start polling every 3 seconds
    pollInterval = setInterval(pollAiStatus, 3000);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [brandSignup.id, aiStatus, pollCount]);

  // Auto-complete when both conditions are met (but only once and only if step not already completed!)
  useEffect(() => {
    if (minimumTimeElapsed && (aiStatus === 'completed' || aiStatus === 'failed') && !isCompleted && !brandSignup.completed_steps?.includes('thinking')) {
      setIsCompleted(true);
      onComplete({});
    }
  }, [minimumTimeElapsed, aiStatus, onComplete, isCompleted]);

  return (
    <div className="step-container">
      <style jsx>{`
        .thinking-container {
          text-align: center;
          padding: 60px 20px;
        }

        .luna-container {
          position: relative;
          margin-bottom: 40px;
        }

        .luna-image {
          width: 140px;
          height: 140px;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .thinking-bubbles {
          position: absolute;
          top: 20px;
          right: calc(50% - 90px);
        }

        .bubble {
          display: inline-block;
          background: #6366f1;
          border-radius: 50%;
          opacity: 0;
          animation: bubble 2s infinite;
          margin-right: 4px;
        }

        .bubble1 {
          width: 8px;
          height: 8px;
          animation-delay: 0s;
        }

        .bubble2 {
          width: 10px;
          height: 10px;
          animation-delay: 0.3s;
        }

        .bubble3 {
          width: 12px;
          height: 12px;
          animation-delay: 0.6s;
        }

        @keyframes bubble {
          0%, 60%, 100% {
            opacity: 0;
            transform: scale(0.5);
          }
          30% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .thinking-title {
          font-size: 28px;
          font-weight: 600;
          margin-bottom: 10px;
          color: #2d3748;
        }

        .thinking-text {
          font-size: 18px;
          color: #4a5568;
          margin-bottom: 30px;
          min-height: 28px;
        }

        .progress-bar {
          max-width: 400px;
          margin: 0 auto 20px;
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
          animation: progress 4s linear;
          border-radius: 3px;
        }

        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }

        .thinking-subtext {
          font-size: 14px;
          color: #718096;
        }

        .skip-link {
          display: block;
          width: 100%;
          text-align: center;
          margin-top: 2rem;
          padding: 0.75rem;
          color: #7f8c8d;
          font-size: 0.9rem;
          text-decoration: none;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .skip-link:hover:not(:disabled) {
          color: #3273dc;
          text-decoration: underline;
        }

        .skip-link:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <div className="thinking-container">
        <div className="luna-container">
          <img
            src="https://assets.nearshored.ai/luna.png"
            alt="Luna AI Assistant"
            className="luna-image"
          />
          <div className="thinking-bubbles">
            <span className="bubble bubble1"></span>
            <span className="bubble bubble2"></span>
            <span className="bubble bubble3"></span>
          </div>
        </div>

        <div className="thinking-content">
          <h2 className="thinking-title">
            Luna is enhancing your RFQ
          </h2>
          <p className="thinking-text">
            {thinkingText}{dots}
          </p>
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p className="thinking-subtext">
            This will just take a moment
          </p>
        </div>

        {onSkipToDashboard && (
          <button
            type="button"
            className="skip-link"
            onClick={onSkipToDashboard}
            disabled={loading}
          >
            I'll set up my project later. Take me to the Dashboard
          </button>
        )}
      </div>
    </div>
  );
};

export default StepThinking;
